/**
 * Operator status-change reasons and their localized labels.
 *
 * The `key` is the English reason text and is the value persisted to
 * Scan.OperatorStatusChangeReason — the database ALWAYS stores English (DL-010).
 * The `label` is the localized display string: en-US renders the English source,
 * he-IL and ru are supplied via XLIFF under the @@reason.<KEY> unit IDs.
 *
 * Hebrew is the canonical operator wording; English and Russian are translations.
 * XLIFF unit IDs are pinned to @@reason.<KEY> — do NOT change them.
 */

/** A single reason entry exposed to the UI. */
export interface OperatorReason {
  /** English reason text — persisted to the database (DL-010). */
  key: string;
  /** Localized display label — use reasonLabel() to resolve. */
  label: string;
}

/**
 * The ordered list of operator reason entries. `label` source strings are en-US;
 * XLIFF carries the he-IL + ru translations under the @@reason.<KEY> unit IDs.
 */
export const OPERATOR_REASONS: OperatorReason[] = [
  { key: 'Camera malfunction', label: $localize`:@@reason.CAMERA_MALFUNCTION:Camera malfunction` },
  { key: 'Rejected', label: $localize`:@@reason.REJECTED:Rejected` },
  { key: 'Sent for re-tinting', label: $localize`:@@reason.SENT_FOR_RETINTING:Sent for re-tinting` },
  { key: 'Sent for re-mixing', label: $localize`:@@reason.SENT_FOR_REMIXING:Sent for re-mixing` },
  { key: 'Laboratory test', label: $localize`:@@reason.LABORATORY_TEST:Laboratory test` },
];

/** Lookup map from key to localized label (built once on module init). */
const REASON_LABEL_MAP = new Map<string, string>(
  OPERATOR_REASONS.map(r => [r.key, r.label])
);

/**
 * Resolves a stored reason key (the English reason text) to its localized label.
 * Returns null when the key is null / undefined / unrecognised — callers
 * should render nothing when null.
 */
export function reasonLabel(key: string | null | undefined): string | null {
  if (!key) return null;
  return REASON_LABEL_MAP.get(key) ?? null;
}
